import type { FileType, IChatAttachmentBrief, ICitation } from "@/api";
import { AttachmentType } from "@/api";
import {
    Download,
    ExternalLink,
    Eye,
    FileSearch2,
    FileSpreadsheet,
    FileText,
    Folder,
    Table,
} from "lucide-react";
import type { FC, ReactNode } from "react";
import type { AttachmentAction } from "./attachment-action";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A unified reference item after deduplication. Used by both user-message and assistant-message surfaces. */
export interface ReferenceItem {
    type: "document" | "scope" | "folder" | "comparison";
    id: number;
    name: string;
    /** Document file type — drives the PDF vs green-spreadsheet pill. Only
     * meaningful when `type === "document"`. */
    fileType?: FileType | null;
    citation?: ICitation;
    attachment?: IChatAttachmentBrief;
}

export interface MenuOption {
    label: string;
    icon: ReactNode;
    action: AttachmentAction;
}

export interface AttachmentStyle {
    bg: string;
    color: string;
    icon: FC<{ className?: string }>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const MAX_VISIBLE = 2;

export const ATTACHMENT_STYLES: Record<string, AttachmentStyle> = {
    scope: { bg: "#D2E5FA", color: "#223876", icon: FileSearch2 },
    folder: { bg: "#E7F5E9", color: "#2E7D32", icon: Folder },
    comparison: { bg: "#e4ddfe", color: "#7c3aed", icon: Table },
    document: { bg: "#faecec", color: "#AF0101", icon: FileText },
};

export const DEFAULT_ATTACHMENT_STYLE = ATTACHMENT_STYLES.scope;

/** A spreadsheet document (xlsx/csv): green + a distinct FileSpreadsheet glyph.
 * A document pill resolves to this or the PDF (document) style by file_type. */
export const SPREADSHEET_ATTACHMENT_STYLE: AttachmentStyle = {
    bg: "#E6F7F0",
    color: "#047857",
    icon: FileSpreadsheet,
};

/**
 * Resolve a reference's pill style. A spreadsheet document (`xlsx`/`csv`) gets
 * the green spreadsheet style; a PDF/legacy document keeps the document red;
 * every other type keeps its per-type style. Spreadsheet-positive — an
 * unknown/absent file_type is treated as a PDF.
 */
export function getReferenceStyle(ref: ReferenceItem): AttachmentStyle {
    if (
        ref.type === "document" &&
        (ref.fileType === "xlsx" || ref.fileType === "csv")
    ) {
        return SPREADSHEET_ATTACHMENT_STYLE;
    }
    return ATTACHMENT_STYLES[ref.type] ?? DEFAULT_ATTACHMENT_STYLE;
}

// ---------------------------------------------------------------------------
// Pure functions (exported for testing)
// ---------------------------------------------------------------------------

/**
 * Deduplicate and merge citations + attachments into a flat reference list.
 *
 * Handles three overlap cases:
 * 1. Scope attachment matching a citation's scope: promotes the document pill to a scope pill.
 * 2. Document attachment matching a citation: merges, keeping the citation for "View Highlights".
 * 3. Multiple citations for the same document: deduplicates by document_id.
 *
 * Also guards against duplicate non-document attachments (same type+id arriving twice)
 * to prevent React key collisions downstream.
 */
export function buildReferences(
    citations: ICitation[],
    attachments: IChatAttachmentBrief[]
): ReferenceItem[] {
    const refs: ReferenceItem[] = [];
    const seenDocIds = new Set<number>();

    for (const citation of citations) {
        if (!seenDocIds.has(citation.document_id)) {
            seenDocIds.add(citation.document_id);
            refs.push({
                type: "document",
                id: citation.document_id,
                name: citation.document_name,
                fileType: citation.file_type,
                citation,
            });
        }
    }

    const seenKeys = new Set<string>(refs.map((r) => `${r.type}:${r.id}`));

    for (const att of attachments) {
        if (att.attachment_type === AttachmentType.Scope) {
            const matchingCitation = citations.find((c) =>
                c.scopes.some((s) => s.scope_id === att.attachment_id)
            );
            if (matchingCitation) {
                const idx = refs.findIndex(
                    (r) =>
                        r.type === "document" &&
                        r.id === matchingCitation.document_id
                );
                if (idx !== -1) {
                    seenKeys.delete(`${refs[idx].type}:${refs[idx].id}`);
                    refs[idx] = {
                        ...refs[idx],
                        type: "scope",
                        id: att.attachment_id,
                        name: att.attachment_name,
                        attachment: att,
                    };
                    seenKeys.add(`scope:${att.attachment_id}`);
                    continue;
                }
            }
        }

        if (att.attachment_type === AttachmentType.Document) {
            const idx = refs.findIndex(
                (r) => r.type === "document" && r.id === att.attachment_id
            );
            if (idx !== -1) {
                refs[idx] = {
                    ...refs[idx],
                    attachment: att,
                    // Prefer the citation's type; fall back to the attachment's.
                    fileType: refs[idx].fileType ?? att.file_type,
                };
                continue;
            }
        }

        const key = `${att.attachment_type}:${att.attachment_id}`;
        if (seenKeys.has(key)) continue;
        seenKeys.add(key);
        refs.push({
            type: att.attachment_type as ReferenceItem["type"],
            id: att.attachment_id,
            name: att.attachment_name,
            fileType: att.file_type,
            attachment: att,
        });
    }

    return refs;
}

export function getMenuOptions(ref: ReferenceItem): MenuOption[] {
    const options: MenuOption[] = [];

    if (ref.type === "document") {
        if (ref.citation) {
            options.push({
                label: "View Highlights",
                icon: <Eye size={16} />,
                action: "view-highlights",
            });
        }
        options.push({
            label: "View Document Library",
            icon: <ExternalLink size={16} />,
            action: "view-document-library",
        });
    } else if (ref.type === "scope") {
        if (ref.citation) {
            options.push({
                label: "View Highlights",
                icon: <Eye size={16} />,
                action: "view-highlights",
            });
        }
        options.push({
            label: "View Full Scope",
            icon: <ExternalLink size={16} />,
            action: "view-scope",
        });
        options.push({
            label: "Export",
            icon: <Download size={16} />,
            action: "export-scope",
        });
    } else if (ref.type === "folder") {
        options.push({
            label: "View Folder",
            icon: <ExternalLink size={16} />,
            action: "view-folder",
        });
    } else if (ref.type === "comparison") {
        options.push({
            label: "View Full Comparison",
            icon: <ExternalLink size={16} />,
            action: "view-comparison",
        });
        options.push({
            label: "Export",
            icon: <Download size={16} />,
            action: "export-comparison",
        });
    }

    return options;
}
